'use client';

import React, { useCallback, useState, useRef } from 'react';
import { cn } from '@/lib/cn';
import { Button } from './Button';
import { IconOpenFolder } from '@repo/svgs';
import { UPLOAD_LIMITS } from '@repo/utils';

export interface UploaderProps {
  /** Existing file count in the parent (used to enforce maxFiles). */
  currentFileCount?: number;
  /** Called with newly added files. Parent owns all file state and previews. */
  onFilesAdded: (files: File[]) => void | Promise<void>;
  maxFiles?: number;
  maxFileSizeMB?: number;
  accept?: string;
  /** Desktop browse button text (inside the dropzone). */
  buttonText?: string;
  /** Mobile button text. */
  mobileButtonText?: string;
  /** Optional handler for validation errors (e.g. to show a toast). */
  onValidationError?: (message: string) => void;
  disabled?: boolean;
  error?: string;
  className?: string;
}

const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'avi', 'mkv', 'flv', 'wmv', 'mpeg', '3gp'] as const;

const ACCEPTED_TYPES = {
  image: ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'application/pdf'],
  // Prefer a wildcard for broad browser/device compatibility; some formats report non-standard MIME types.
  video: ['video/*'],
};

export function Uploader({
  onFilesAdded,
  currentFileCount = 0,
  maxFiles = UPLOAD_LIMITS.MAX_FILES,
  maxFileSizeMB = UPLOAD_LIMITS.MAX_FILE_SIZE_MB,
  accept = `${ACCEPTED_TYPES.image.join(',')},${ACCEPTED_TYPES.video.join(',')},${VIDEO_EXTENSIONS.map(
    e => `.${e}`,
  ).join(',')}`,
  buttonText = 'Browse files',
  mobileButtonText = 'Upload photos and videos',
  onValidationError,
  disabled = false,
  error,
  className,
}: UploaderProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationError, setValidationError] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isAcceptedByAccept = useCallback(
    (file: File) => {
      const a = (accept || '').trim();
      if (!a) return true;
      const parts = a
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      if (parts.length === 0) return true;

      const fileType = (file.type || '').toLowerCase();
      const name = (file.name || '').toLowerCase();

      return parts.some(p => {
        const part = p.toLowerCase();
        if (part === '*/*') return true;
        if (part.endsWith('/*')) {
          const prefix = part.slice(0, part.length - 1); // keep trailing '/'
          return fileType.startsWith(prefix);
        }
        if (part.startsWith('.')) {
          return name.endsWith(part);
        }
        // exact mime type
        return fileType === part;
      });
    },
    [accept],
  );

  const validateFiles = useCallback(
    (fileList: FileList | File[]): { valid: File[]; invalid: string[] } => {
      const filesArray = Array.from(fileList);
      const valid: File[] = [];
      const invalid: string[] = [];

      // Check total count
      if (currentFileCount + filesArray.length > maxFiles) {
        invalid.push(`Maximum ${maxFiles} files allowed`);
        return { valid, invalid };
      }

      filesArray.forEach(file => {
        // Check file size
        const sizeMB = file.size / (1024 * 1024);
        if (sizeMB > maxFileSizeMB) {
          invalid.push(`${file.name}: File size exceeds ${maxFileSizeMB}MB`);
          return;
        }

        // Check file type based on accept prop
        if (!isAcceptedByAccept(file)) {
          invalid.push(`${file.name}: Unsupported file type`);
          return;
        }

        valid.push(file);
      });

      return { valid, invalid };
    },
    [currentFileCount, isAcceptedByAccept, maxFiles, maxFileSizeMB],
  );

  const processFiles = useCallback(
    async (fileList: FileList | File[]) => {
      const { valid, invalid } = validateFiles(fileList);

      if (invalid.length > 0) {
        const message = invalid.join('; ');
        onValidationError?.(message);

        // For max-files errors, prefer toast UX over red-border inline state.
        // (Parents can still pass `error` explicitly if they want a persistent inline error.)
        if (
          invalid.some(
            s => s.toLowerCase().includes('maximum') && s.toLowerCase().includes('files'),
          )
        ) {
          setValidationError(undefined);
        } else {
          setValidationError(message);
        }
        return;
      }

      setValidationError(undefined);
      await onFilesAdded(valid);
    },
    [onFilesAdded, onValidationError, validateFiles],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      if (disabled) return;

      const { files: droppedFiles } = e.dataTransfer;
      if (droppedFiles.length > 0) {
        await processFiles(droppedFiles);
      }
    },
    [disabled, processFiles],
  );

  const handleFileInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files && files.length > 0) {
        await processFiles(files);
      }
      // Reset input value to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [processFiles],
  );

  const handleBrowseClick = useCallback(() => {
    if (!disabled && currentFileCount < maxFiles && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [currentFileCount, disabled, maxFiles]);

  const handleContainerActivate = useCallback(() => {
    handleBrowseClick();
  }, [handleBrowseClick]);

  const isCompactInline = currentFileCount > 0;
  const isAtMaxFiles = currentFileCount >= maxFiles;

  const displayError = error || validationError;

  return (
    <div className={cn('flex w-full flex-col gap-4', className)}>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={accept}
        onChange={handleFileInputChange}
        className="hidden"
        disabled={disabled || isAtMaxFiles}
      />

      {/* When max files reached: hide uploader UI and show a banner */}
      {isAtMaxFiles ? (
        <div className="w-full bg-secondary rounded-2xl py-4 px-6 text-body-2 text-secondary text-center">
          Max {maxFiles} files
        </div>
      ) : null}

      {/* Upload Area - Desktop: Drag & Drop, Mobile: Button */}
      {!isAtMaxFiles ? (
        <div className="flex w-full flex-col gap-4">
          {/* Desktop drag and drop area */}
          <div
            className={cn(
              'hidden bg-secondary md:flex w-full flex-col items-center justify-center gap-4 text-center p-6',
              'border-2 border-dashed rounded-2xl transition-colors cursor-pointer',
              disabled
                ? 'cursor-not-allowed bg-secondary border-secondary'
                : isDragOver
                  ? 'border-accent bg-accent/5'
                  : displayError
                    ? 'border-alert bg-alert/5'
                    : 'border-secondary bg-primary hover:bg-primary-hover',
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled || undefined}
            onClick={handleContainerActivate}
            onKeyDown={e => {
              if (disabled) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleContainerActivate();
              }
            }}
          >
            <div
              className={cn(
                'flex items-center justify-center gap-4',
                isCompactInline ? 'flex-row' : 'flex-col',
              )}
            >
              <p
                className={cn('text-body-2 text-secondary', isCompactInline && 'whitespace-nowrap')}
              >
                Drag and drop files
              </p>
              <p
                className={cn('text-body-2 text-secondary', isCompactInline && 'whitespace-nowrap')}
              >
                – or –
              </p>
              <Button
                variant="tertiary"
                size="md"
                onPointerDown={e => e.stopPropagation()}
                onClick={e => {
                  e.stopPropagation();
                  handleBrowseClick();
                }}
                iconLeft={<IconOpenFolder className="h-4 w-4 icon-accent" />}
                disabled={disabled}
                className="!text-accent !body-2-high !px-4 !py-3"
              >
                {buttonText}
              </Button>
            </div>
          </div>

          {/* Mobile upload button */}
          <div
            className={cn(
              'md:hidden !bg-secondary block w-full flex-col items-center justify-center gap-4 text-center p-6',
              'border-2 border-dashed rounded-2xl transition-colors cursor-pointer',
              disabled
                ? 'cursor-not-allowed bg-secondary border-secondary'
                : isDragOver
                  ? 'border-accent bg-accent/5'
                  : displayError
                    ? 'border-alert bg-alert/5'
                    : 'border-secondary bg-primary hover:bg-primary-hover',
            )}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled || undefined}
            onClick={handleContainerActivate}
            onKeyDown={e => {
              if (disabled) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleContainerActivate();
              }
            }}
          >
            <Button
              variant="tertiary"
              size="md"
              onPointerDown={e => e.stopPropagation()}
              onClick={e => {
                e.stopPropagation();
                handleBrowseClick();
              }}
              disabled={disabled}
              className="!text-accent !body-2-high"
            >
              <IconOpenFolder className="h-4 w-4 mr-2 icon-accent" />
              {buttonText}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
